import React, { useState, useEffect } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';
import PropTypes from 'prop-types';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { registerUserRequest } from '../../services/user.api.service';
import AccountCreationForEmailRegistrations from '../account_creation_for_email_registrations/account_creation_for_email_registrations';
import VerificationEmailSent from '../verification_email_sent/verification_email_sent';
import ExpiredEmail from '../expired_email/expired_email';
import GetStarted from '../get_started/get_started';
import DefaultRegisterForm from '../default_register_form/default_register_form';
import MembershipFlowContext from '../../context/membership_flow_context';
import Spinner from '../../../components/react/spinner/spinner';
import YourDetailsForDefaultReg from '../your_details_for_default_reg/your_details_for_default_reg';
import UpgradeToCaPitch from '../upgrade_to_ca_pitch/upgrade_to_ca_pitch';
import Newsletters from '../newsletters/newsletters';
import { CM_PACKAGE_SHORT_NAME } from '../../constants/membership_flow.constants';
import { createQueryString } from '../../../utilities/utilities';

import './default_registration.scss';

const DefaultRegistration = ({
  text1,
  text2,
  packageShortName,
  authToken,
  paymentPage,
  returnTo,
  country,
  isSignedIn,
  isUpgradeAllowedForReturningSocialMediaUsers,
}) => {
  const [waitingForEarlyRedirectionCheck, setWaitingForEarlyRedirectionCheck] = useState(true);
  // When going to Expired url we want the email from the url
  const emailFromUrl = useQueryParam('email', StringParam);
  const [emailRecipient, setEmailRecipient] = useState(emailFromUrl[0] || '');

  const yourDetailsRoutes = useRouteMatch('/register/*/your-details');
  const upgradeToCaPitchRoute = useRouteMatch('/register/cm/upgrade-to-ca');

  useEffect(() => {
    // Fast checkout a.k.a. skip_optional_steps mechanism
    // If it is a fast checkout, don't ask CMs to upgrade to CA, sends them to their redirect
    // before they even can see the screen.
    if (upgradeToCaPitchRoute) {
      const skipOptionalSteps = decodeURIComponent(returnTo).includes('skip_optional_steps=true');

      if (skipOptionalSteps) {
        window.location.href = returnTo || '/';
        // On redirection, leave the useEffect check
        return;
      }
    }

    // Logic related to '/register/:packageShortName/your-details' routes
    if (yourDetailsRoutes) {
      // For returning users who created their account via social media login,
      // once they go to `/register/:packageShortName/your-details`, skip Your Details step.

      // We search the entire string because the parameter may be inside a return_to
      const consultantExists = decodeURIComponent(window.location.search).includes(
        'consultant_exists=true'
      );

      if (consultantExists) {
        // Returning social media users expecting a CM package
        // Return them to the home page or the return_to if present
        // They don't need to repeat the YourDetails step
        if (packageShortName === CM_PACKAGE_SHORT_NAME) {
          window.location.href = returnTo || '/';
          // On redirection, leave the useEffect check
          return;
        }

        // Returning social media users expecting to get a CA, PRO or PFO package but
        // already have a paid package equal or above what they want, send them to their redirect.
        // These users also skip the YourDetails step and expect to go to Upgrade but they cannot.
        if (!isUpgradeAllowedForReturningSocialMediaUsers) {
          window.location.href = returnTo || '/';
          // On redirection, leave the useEffect check
          return;
        }

        // Returning social media users epxecting to get a CA, PRO or PFO package
        // If their current package is compatible with what they want, send them to the UpgradeFlow.
        window.location.href =
          '/upgrade/payment' +
          createQueryString({
            package_short_name: packageShortName,
            payment_page_permalink: paymentPage,
            return_to: returnTo,
          });
        // On redirection, leave the useEffect check
        return;
      }
    }
    // If user is not located in `*/your-details` nor `*/upgrade-to-ca`, exit this logic and stop
    // loading effect
    setWaitingForEarlyRedirectionCheck(false);
  }, [upgradeToCaPitchRoute, yourDetailsRoutes]);

  // Method to be executed in ExpiredEmail and VerificationEmailSent components
  const postRegistrationRequest = () =>
    registerUserRequest({
      email: emailRecipient,
      packageShortName,
      paymentPage,
      returnTo,
    });

  const loadingSpinner = (
    <div className='d-flex align-items-center justify-content-center my-6'>
      <Spinner isLoading isAbsolute />
    </div>
  );

  if (waitingForEarlyRedirectionCheck) return loadingSpinner;

  return (
    <div className='generic-signup' data-testid='default-registration'>
      <MembershipFlowContext.Provider
        value={{
          text1,
          text2,
          packageShortName,
          authToken,
          paymentPage,
          returnTo,
          country,
          isSignedIn,
        }}
      >
        <Switch>
          {/* Account creation after email verification `/register/:package/account-setup` */}
          <Route path='*/account-setup'>
            <AccountCreationForEmailRegistrations />
          </Route>
          {/* Your Details step for all users `/register/:package/your-details` */}
          <Route path='*/your-details'>
            <YourDetailsForDefaultReg
              setWaitingForEarlyRedirectionCheck={setWaitingForEarlyRedirectionCheck}
            />
          </Route>
          {/* When initial intention is a CM package, after `/register/cm/your-details`,
            those users go to `/register/cm/upgrade-to-ca` */}
          <Route path='*/upgrade-to-ca'>
            <UpgradeToCaPitch />
          </Route>
          {/* When initial intention is a CM package, if users refuse to upgrade in
            `/register/cm/upgrade-to-ca`, send them to `/register/cm/newsletters` */}
          <Route path='*/newsletters'>
            <Newsletters />
          </Route>
          {/* Registration before email verification */}
          <Route path='*/expired'>
            <ExpiredEmail
              emailRecipient={emailRecipient}
              registerUserRequest={postRegistrationRequest}
            />
          </Route>
          <Route path='*/verification-sent'>
            <VerificationEmailSent
              emailRecipient={emailRecipient}
              registerUserRequest={postRegistrationRequest}
            />
          </Route>
          {/* GetStarted is only rendered when AAs go to /regster/PRO or /register/CA */}
          <Route path='*/get-started'>
            <GetStarted />
          </Route>
          <Route path=''>
            <DefaultRegisterForm setEmailRecipient={setEmailRecipient} />
          </Route>
        </Switch>
      </MembershipFlowContext.Provider>
    </div>
  );
};

DefaultRegistration.propTypes = {
  text1: PropTypes.string,
  text2: PropTypes.string,
  packageShortName: PropTypes.string,
  authToken: PropTypes.string,
  paymentPage: PropTypes.string,
  returnTo: PropTypes.string,
  country: PropTypes.object,
  isSignedIn: PropTypes.bool.isRequired,
  isUpgradeAllowedForReturningSocialMediaUsers: PropTypes.bool,
};

DefaultRegistration.defaultProps = {
  text1: '',
  text2: '',
  authToken: '',
  paymentPage: '',
  returnTo: '', // params[return_to] that comes in the url when subscribing
  country: {},
};

export default DefaultRegistration;
